<script lang="ts">
    import { _ } from "svelte-i18n"
    import { mdiEmailOutline, mdiFactory, mdiPhone, mdiLinkedin, mdiInstagram, mdiFacebook, mdiMapMarkerOutline, mdiCookie } from "@mdi/js"
    import Icon from "../../widgets/Icon.svelte"
    import { socialMedia } from "../../../configs/config"
    import { preselectedDealerNumber } from "../../../functionTools/iframeIntegration"
    import { getCachedEntries } from "../../../api"
    import { initialLocale } from "../../../i18n"
    import { spaLink } from "../../../functionTools/navigation"
    import { openConsentBanner } from "../../../lib/consent"

    let navigation: NavigationNode[] = $state([])

    function getPath(entry: any) {
        if (entry?.external) {
            return entry.externalUrl
        }
        return entry?.path
    }

    async function loadNavigation() {
        const entries: NavigationTree[] = await getCachedEntries("navigation")
        const footerEntry = entries.find((e) => e.treeName === "footer" && e.language === initialLocale)
        navigation = footerEntry?.nodes || []
    }

    loadNavigation()
</script>

<footer id="footer">
    <div class="footer-container">
        <section class="footer_menus">
            <div class="row upper">
                <div class="">
                    <h3>{$_("footer.contact")}</h3>
                    <ul>
                        <li>
                            <span><Icon size="24px" path={mdiFactory} /></span>
                            <p>Erwin Renz Metallwarenfabrik GmbH & Co KG</p>
                        </li>
                        <li>
                            <span><Icon size="24px" path={mdiMapMarkerOutline} /></span>
                            <p>Boschstraße 3<br />71737 Kirchberg an der Murr</p>
                        </li>
                        <li>
                            <span><Icon size="24px" path={mdiPhone} /></span>
                            <a href="tel:+7144 3010">07144 3010</a>
                        </li>
                        <li>
                            <span><Icon size="24px" path={mdiEmailOutline} /></span>
                            <a href="mailto:info@renz24.de">info@renz24.de</a>
                        </li>
                    </ul>
                </div>

                <div class="">
                    <h3>{$_("footer.social-media")}</h3>
                    <ul class="icons">
                        <li>
                            <a href={socialMedia.linkedIn} aria-label="linkedIn" target="_blank"
                                ><Icon size="24px" path={mdiLinkedin} /></a
                            >
                        </li>
                        <li>
                            <a href={socialMedia.instagram} aria-label="Instagram" target="_blank"
                                ><Icon size="24px" path={mdiInstagram} /></a
                            >
                        </li>

                        <li>
                            <a href={socialMedia.facebook} aria-label="Facebook" target="_blank"
                                ><Icon size="24px" path={mdiFacebook} /></a
                            >
                        </li>

                        <li>
                            <a href={socialMedia.xing} aria-label="Xing" target="_blank"
                                ><img style="height: 30px;" src="/media/xing.svg" alt="xing" /></a
                            >
                        </li>
                    </ul>
                </div>

                <div class="">
                    <h3>{$_("footer.we-ship-with")}</h3>
                    <div class="dpd-box box">
                        <span><img src="/media/dpd.svg" loading="lazy" alt="DPD" /></span>
                    </div>
                </div>
            </div>
            <div class="row">
                {#if !$preselectedDealerNumber}
                    {#each navigation as column}
                        <div class="">
                            <h3>{column.name}</h3>
                            <ul>
                                {#each column.nodes as link}
                                    <li>
                                        {#if link.external}
                                            <a
                                                href={link.externalUrl}
                                                target={link.target || "_self"}
                                                rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                                            >
                                                {link.name}
                                            </a>
                                        {:else}
                                            <a href={getPath(link)} use:spaLink>
                                                {link.name}
                                            </a>
                                        {/if}
                                    </li>
                                {/each}
                            </ul>
                        </div>
                    {/each}
                {/if}
            </div>
            <div class="row legal">
                <div>
                    <button
                        type="button"
                        class="consent-settings"
                        data-testid="consent-settings"
                        onclick={() => openConsentBanner()}
                    >
                        <span><Icon size="24px" path={mdiCookie} /></span>
                        {$_("consent.settings")}
                    </button>
                </div>
            </div>
        </section>
    </div>
</footer>

<style lang="less">
    @import "../../../css/variables.less";

    #footer {
        background: var(--grey-500);
        color: var(--comp--footer--color);
        font-size: 0.9em;
        padding: 60px 0px;
        position: relative;
        width: 100%;
        display: flex;
        justify-content: center;
        .footer-container {
            max-width: var(--small-container-width);
            width: 100%;
            margin: 0 1.2rem;

            h3 {
                font-size: 1rem;
            }
            .footer_menus {
                margin-top: 40px;
                display: flex;
                flex-direction: column;
                gap: 2.4rem;
                h3 {
                    font-weight: 700;
                    border-bottom: solid 1px var(--border-color-default);
                    margin: 0 0 20px 0;
                    padding-bottom: 6px;
                }
                ul {
                    list-style-type: none;
                    margin: 0 0 30px 0;
                    padding: 0;

                    li {
                        @media @mobile_only {
                            padding-top: 10px;
                            padding-bottom: 10px;
                        }
                        a {
                            font-size: 0.8rem;
                            color: var(--comp--footer--color);
                            display: block;
                            margin-bottom: 5px;
                            &:hover {
                                text-decoration: underline;
                            }
                        }
                    }
                }
                .row {
                    display: grid;
                    margin: 0px;
                    grid-template-columns: 1fr;
                    @media (min-width: 500px) {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    @media (min-width: 800px) {
                        grid-template-columns: repeat(4, 1fr);
                    }
                    gap: 2.4rem;
                    & > div {
                        padding: 0px;
                        width: 100%;
                    }
                }

                .upper {
                    & > div {
                        ul {
                            display: flex;
                            flex-direction: column;
                            gap: 5px;
                            @media (min-width: @res-desktop) {
                                gap: 20px;
                            }

                            li {
                                display: flex;
                                gap: 10px;
                                margin: 0px;
                                align-items: start;

                                p,
                                a {
                                    margin: 0px;
                                    font-size: 1rem;
                                    display: flex;
                                    align-items: flex-end;
                                }
                                img {
                                    height: 15px;
                                }
                            }
                            &.icons {
                                flex-direction: row;

                                flex-wrap: wrap;
                                gap: 15px;
                            }
                        }
                        .box {
                            padding: 10px 15px;
                            display: flex;
                            align-items: center;
                            gap: 15px;
                            border-radius: 5px;
                            width: 100%;
                            min-height: 78px;
                            &.dpd-box {
                                justify-content: center;
                                img {
                                    height: 40px;
                                }
                                background-color: white;
                            }
                        }
                    }
                }

                .legal {
                    border-top: solid 1px var(--border-color-default);
                    padding-top: 1rem;

                    .consent-settings {
                        background: none;
                        border: none;
                        padding: 0;
                        cursor: pointer;
                        font-size: 0.8rem;
                        color: var(--comp--footer--color);
                        // Wie alle Footer-Links: keine Unterstreichung, nur bei Hover.
                        text-decoration: none;
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;

                        &:hover {
                            text-decoration: underline;
                        }

                        &:focus-visible {
                            outline: 3px solid var(--primary);
                            outline-offset: 2px;
                        }
                    }
                }
            }
        }
    }
</style>
